import { Component, PureComponent } from "react";

import { StatusDisplay, type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import { BillDocumentType } from "entity/bill/Bill/Bill.api.types";

export enum BillStatusType {
    Open = 0,
    ReadyForPayment = 1,
    Paid = 2,
    Void = 3,
    UnderSubReview = 4,
    OnlinePaymentProcessing = 5,
    OnlinePaymentDeclined = 6,
    OnlinePaymentFailed = 7,
    InReview = 8,
    Draft = 9,
    NotApplicable = -1,
    Requested = -2,
}

export enum BillPaidStatusType {
    Pending = 1,
    Voided = 2,
    FullyPaid = 3,
    PartiallyPaid = 4,
    NotPaid = 5,
    PaymentRequestedBySub = 6,
    ReadyForPayment = 7,
}

export const BillStatusTypeDescription = new Map<BillStatusType, string>([
    [BillStatusType.Open, "Open"],
    [BillStatusType.ReadyForPayment, "Ready For Payment"],
    [BillStatusType.Paid, "Paid"],
    [BillStatusType.Void, "Void"],
    [BillStatusType.UnderSubReview, "Under Sub/Vendor Review"],
    [BillStatusType.OnlinePaymentProcessing, "Online Payment Processing"],
    [BillStatusType.OnlinePaymentDeclined, "Online Payment Declined"],
    [BillStatusType.OnlinePaymentFailed, "Online Payment Failed"],
    [BillStatusType.NotApplicable, "N/A"],
    [BillStatusType.InReview, "In Review"],
    [BillStatusType.Draft, "Draft"],
]);

/**
 * Vendor-credit-specific status text. Only statuses whose wording differs for a
 * credit appear here; everything else falls back to the bill text. Shared single
 * source of truth — also consumed by the PO-payment mapper.
 */
export const VendorCreditStatusOverride = new Map<BillStatusType, string>([
    [BillStatusType.ReadyForPayment, "Available"],
    [BillStatusType.Paid, "Applied"],
]);

export const getStatusText = (
    status: BillStatusType,
    isSubRequested?: boolean,
    documentType?: BillDocumentType,
): string => {
    if (documentType === BillDocumentType.VendorCredit) {
        const creditText = VendorCreditStatusOverride.get(status);
        if (creditText !== undefined) {
            return creditText;
        }
    }
    let statusTagText = BillStatusTypeDescription.get(status)!;
    if (isSubRequested && status === BillStatusType.Open) {
        statusTagText = "Requested";
    }
    if (status === BillStatusType.NotApplicable) {
        statusTagText = BillStatusTypeDescription.get(BillStatusType.Open)!;
    }
    return statusTagText;
};

export const BillStatusTypeType = (status: BillStatusType): StatusTypes => {
    switch (status) {
        case BillStatusType.Paid:
            return "success";
        case BillStatusType.Void:
        case BillStatusType.OnlinePaymentDeclined:
        case BillStatusType.OnlinePaymentFailed:
            return "danger";
        case BillStatusType.UnderSubReview:
        case BillStatusType.OnlinePaymentProcessing:
        case BillStatusType.InReview:
            return "pending";
        default:
            return "";
    }
};

interface IBillStatusProps {
    status: BillStatusType;
    isSubRequested: boolean;
    details?: string | JSX.Element | null;
    documentType?: BillDocumentType;
}

export class BillStatus extends Component<IBillStatusProps> {
    render() {
        const { status, isSubRequested, documentType } = this.props;
        if (status < -1 || status > BillStatusTypeDescription.size - 1) {
            return "";
        } else {
            return (
                <StatusDisplay
                    statusType={BillStatusTypeType(status)}
                    details={this.props.details}
                    data-testid="statusTextContainer"
                >
                    {getStatusText(status, isSubRequested, documentType)}
                </StatusDisplay>
            );
        }
    }
}

interface IBillStatusTagProps {
    status: BillStatusType;
    statusText?: string;
    isSubRequested?: boolean;
    useBds: boolean;
    documentType?: BillDocumentType;
}

export class BillStatusTag extends PureComponent<IBillStatusTagProps> {
    render() {
        const { status, statusText, isSubRequested, useBds, documentType } = this.props;
        return (
            <StatusTagDisplay
                statusText={statusText || getStatusText(status, isSubRequested, documentType)}
                statusType={BillStatusTypeType(status)}
                useBds={useBds}
            />
        );
    }
}
